import { Flex, Icon, Text } from '@/ui';
import { useDropdown } from '../useDropdown';

type Props = {
  isDefault?: boolean;
  isRemoving?: boolean;
  onChangeLayout: () => void;
  onRemoveView: () => void;
  onToggleDefault: () => void;
};

export const ViewMoreMenu = ({
  isDefault,
  isRemoving,
  onChangeLayout,
  onRemoveView,
  onToggleDefault,
}: Props) => {
  const { close, isOpen, ref, toggle } = useDropdown();

  return (
    <Flex alignSelf="stretch" position="relative" ref={ref}>
      <Flex
        _hover={{ bg: 'rgba(0,0,0,0.05)', borderColor: 'rgba(0,0,0,0.16)' }}
        align="center"
        alignSelf="stretch"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="More options"
        as="button"
        bg="white"
        borderColor={isOpen ? 'rgba(0,0,0,0.16)' : 'line.DEFAULT'}
        borderRadius="ah-md"
        borderStyle="solid"
        borderWidth="1px"
        cursor="pointer"
        justify="center"
        minW="38px"
        transition="background 0.14s, border-color 0.14s"
        type="button"
        onClick={toggle}
      >
        <Icon color="ink.soft" icon="MoreHorizontal" size={4} />
      </Flex>

      {isOpen && (
        <Flex
          bg="white"
          borderColor="line.DEFAULT"
          borderRadius="ah-xl"
          borderStyle="solid"
          borderWidth="1px"
          boxShadow="0 4px 12px rgba(0,0,0,0.08)"
          direction="column"
          mt={1.5}
          overflow="hidden"
          position="absolute"
          py={1}
          right={0}
          top="100%"
          w="200px"
          zIndex={10}
        >
          <Flex
            _hover={{ bg: 'rgba(0,0,0,0.03)' }}
            align="center"
            as="button"
            cursor="pointer"
            gap={2.5}
            px={3}
            py={2}
            role="menuitem"
            textAlign="left"
            type="button"
            w="full"
            onClick={() => {
              close();
              onChangeLayout();
            }}
          >
            <Icon color="ink.soft" icon="Layout" size={4} />
            <Text color="ink.strong" fontSize="ah-control" fontWeight={500}>
              Change layout
            </Text>
          </Flex>
          <Flex
            _hover={{ bg: 'rgba(0,0,0,0.03)' }}
            align="center"
            as="button"
            cursor="pointer"
            gap={2.5}
            px={3}
            py={2}
            role="menuitem"
            textAlign="left"
            type="button"
            w="full"
            onClick={() => {
              close();
              onToggleDefault();
            }}
          >
            <Icon
              color="ink.soft"
              icon={isDefault ? 'StarOff' : 'Star'}
              size={4}
            />
            <Text color="ink.strong" fontSize="ah-control" fontWeight={500}>
              {isDefault ? 'Remove as default view' : 'Set as default view'}
            </Text>
          </Flex>
          <Flex
            _hover={{ bg: 'destructive.50' }}
            align="center"
            as="button"
            cursor={isRemoving ? 'not-allowed' : 'pointer'}
            disabled={isRemoving}
            gap={2.5}
            opacity={isRemoving ? 0.5 : 1}
            px={3}
            py={2}
            role="menuitem"
            textAlign="left"
            type="button"
            w="full"
            onClick={() => {
              close();
              onRemoveView();
            }}
          >
            <Icon color="destructive.500" icon="Trash" size={4} />
            <Text
              color="destructive.500"
              fontSize="ah-control"
              fontWeight={500}
            >
              {isRemoving ? 'Removing…' : 'Remove view'}
            </Text>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
